import { Metrics, MetricValueContext, METRIC_KEY } from "calculation/metric";
import { Metric } from "components/zoning-plan-common/Metric";
import { SubMetric } from "components/zoning-plan-common/SubMetric";
import { MunicipalityDto, ScenarioDto } from "dtos/dtos";
import { ReactElement } from "react";
import { CalculationState } from "state/state";

export function CalculationTable(props: {
  metrics: Metrics;
  scenario: ScenarioDto;
  calculationState: CalculationState;
  municipality: MunicipalityDto;
}): ReactElement {
  const { metrics, scenario, calculationState, municipality } = props;

  return (
    <>
      <Metric
        name={metrics.getMetric(METRIC_KEY.pVarFactorRp)!.name}
        description={metrics.getMetric(METRIC_KEY.pVarFactorRp)!.description}
        unit={metrics.getMetric(METRIC_KEY.pVarFactorRp)!.unit}
        value={
          calculationState.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.pVarFactorRp, {
              name: "municipality",
              bfsNr: municipality.bfsNr,
            })
          )!.value
        }
        format={true}
      />
      <Metric
        name={metrics.getMetric(METRIC_KEY.populationHist)!.name}
        description={metrics.getMetric(METRIC_KEY.populationHist)!.description}
        unit={metrics.getMetric(METRIC_KEY.populationHist)!.unit}
        value={
          calculationState.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.populationHist, {
              name: "municipality",
              bfsNr: municipality.bfsNr,
            })
          )!.value
        }
        format={true}
      />
      <Metric
        name={metrics.getMetric(METRIC_KEY.qPopulationFuture)!.name}
        description={
          metrics.getMetric(METRIC_KEY.qPopulationFuture)!.description
        }
        unit={metrics.getMetric(METRIC_KEY.qPopulationFuture)!.unit}
        value={
          calculationState.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.qPopulationFuture, {
              name: "municipality",
              bfsNr: municipality.bfsNr,
            })
          )!.value
        }
        format={true}
      />
      <Metric
        name={metrics.getMetric(METRIC_KEY.aPopulationActual)!.name}
        description={
          metrics.getMetric(METRIC_KEY.aPopulationActual)!.description
        }
        unit={metrics.getMetric(METRIC_KEY.aPopulationActual)!.unit}
        value={
          calculationState.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.aPopulationActual, {
              name: "municipality",
              bfsNr: municipality.bfsNr,
            })
          )!.value
        }
        format={true}
      />
      <Metric
        name={metrics.getMetric(METRIC_KEY.bPopulationCapacityTotal)!.name}
        description={
          metrics.getMetric(METRIC_KEY.bPopulationCapacityTotal)!.description
        }
        unit={metrics.getMetric(METRIC_KEY.bPopulationCapacityTotal)!.unit}
        value={
          calculationState.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.bPopulationCapacityTotal, {
              name: "municipality",
              bfsNr: municipality.bfsNr,
            })
          )!.value
        }
        format={true}
      />
      <Metric
        name={metrics.getMetric(METRIC_KEY.cPopulationGrowth)!.name}
        description={
          metrics.getMetric(METRIC_KEY.cPopulationGrowth)!.description
        }
        unit={metrics.getMetric(METRIC_KEY.cPopulationGrowth)!.unit}
        value={
          calculationState.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.cPopulationGrowth, {
              name: "municipality",
              bfsNr: municipality.bfsNr,
            })
          )!.value
        }
        format={true}
      >
        <SubMetric
          name="in %"
          value={
            calculationState.metricValues.getValue(
              new MetricValueContext(METRIC_KEY.dPopulationGrowthPercent, {
                name: "municipality",
                bfsNr: municipality.bfsNr,
              })
            )!.value
          }
        />
        <SubMetric
          name="pro Jahr"
          value={
            calculationState.metricValues.getValue(
              new MetricValueContext(METRIC_KEY.ePopulationGrowthAnnual, {
                name: "municipality",
                bfsNr: municipality.bfsNr,
              })
            )!.value
          }
        />
        <SubMetric
          name="pro Jahr in %"
          value={
            calculationState.metricValues.getValue(
              new MetricValueContext(
                METRIC_KEY.fPopulationGrowthAnnualPercent,
                {
                  name: "municipality",
                  bfsNr: municipality.bfsNr,
                }
              )
            )!.value
          }
        />
      </Metric>
      <Metric
        name={metrics.getMetric(METRIC_KEY.lBuildingAreaDemandActual)!.name}
        description={
          metrics.getMetric(METRIC_KEY.lBuildingAreaDemandActual)!.description
        }
        unit={metrics.getMetric(METRIC_KEY.lBuildingAreaDemandActual)!.unit}
      >
        {scenario.districts.map((d) => (
          <SubMetric
            key={d.gem1999}
            name={`Ortsteil ${d.gem1999}`}
            value={
              calculationState!.metricValues.getValue(
                new MetricValueContext(METRIC_KEY.lBuildingAreaDemandActual, {
                  name: "district",
                  bfsNr: municipality!.bfsNr,
                  bfsNr1999: d.bfsNr1999,
                })
              )!.value
            }
          />
        ))}
      </Metric>
      <Metric
        name={metrics.getMetric(METRIC_KEY.mBuildingAreaDemandExpected)!.name}
        description={
          metrics.getMetric(METRIC_KEY.mBuildingAreaDemandExpected)!.description
        }
        unit={metrics.getMetric(METRIC_KEY.mBuildingAreaDemandExpected)!.unit}
      >
        {scenario.districts.map((d) => (
          <SubMetric
            key={d.gem1999}
            name={`Ortsteil ${d.gem1999}`}
            value={
              calculationState!.metricValues.getValue(
                new MetricValueContext(METRIC_KEY.mBuildingAreaDemandExpected, {
                  name: "district",
                  bfsNr: municipality!.bfsNr,
                  bfsNr1999: d.bfsNr1999,
                })
              )!.value
            }
          />
        ))}
      </Metric>
      <Metric
        name={metrics.getMetric(METRIC_KEY.oBuildingAreaDemandComparison)!.name}
        description={
          metrics.getMetric(METRIC_KEY.oBuildingAreaDemandComparison)!
            .description
        }
        unit={metrics.getMetric(METRIC_KEY.oBuildingAreaDemandComparison)!.unit}
      >
        {scenario.districts.map((d) => (
          <SubMetric
            key={d.gem1999}
            name={`Ortsteil ${d.gem1999}`}
            value={
              calculationState!.metricValues.getValue(
                new MetricValueContext(
                  METRIC_KEY.oBuildingAreaDemandComparison,
                  {
                    name: "district",
                    bfsNr: municipality!.bfsNr,
                    bfsNr1999: d.bfsNr1999,
                  }
                )
              )!.value
            }
          />
        ))}
      </Metric>
      <Metric
        name={metrics.getMetric(METRIC_KEY.rPopulationNewZonesDemand)!.name}
        description={
          metrics.getMetric(METRIC_KEY.rPopulationNewZonesDemand)!.description
        }
        unit={metrics.getMetric(METRIC_KEY.rPopulationNewZonesDemand)!.unit}
        value={
          calculationState.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.rPopulationNewZonesDemand, {
              name: "municipality",
              bfsNr: municipality.bfsNr,
            })
          )!.value
        }
        format={true}
      />
    </>
  );
}
